import {useMemo, useSyncExternalStore} from 'react';

import type {Participant, ParticipantID, RoomID} from '@pexip/infinity';
import {MAIN_ROOM_ID} from '@pexip/infinity';
import type {
    ChatMessage,
    DirectChatMessages,
    InMeetingParticipant,
    UnseenUnreadDirectChatMessages,
} from '@pexip/media-components';
import {ChatType, useAssertedContext} from '@pexip/media-components';
import {memoize} from '@pexip/utils';

import {applicationConfig} from '../applicationConfig';
import {useConfigContext} from '../config';
import {MeetingContext} from '../contexts/meeting.context';
import type {Meeting} from '../services/InfinityClient.service';
import {callSignals} from '../signals/Call.signals';
import {infinityClientSignals} from '../signals/InfinityClient.signals';
import {participantSearchQuerySignal} from '../signals/InMeeting.signals';
import {
    breakoutsAskingForHelpSignal,
    chatMessagesSignal,
    directChatMessagesSignal,
    guestTransferDetailsSignal,
    hostReferDetailsSignal,
    idpSignal,
    infinityErrorSignal,
    isTogglingLiveCaptionsSignal,
    layoutOverlayTextEnabledSignal,
    liveCaptionsEnabledSignal,
    liveCaptionsSignal,
    pinRequiredSignal,
    remoteStreamSignal,
    selectedCaptionsLanguageSignal,
    splashScreenSignal,
    stepSignal,
    unreadChatMessagesSignal,
    unreadDirectChatMessagesSignal,
    unseenUnreadDirectChatMessagesSignal,
    participantAudioStreamsSignal,
    stageParticipantIdsSignal,
    participantVideoStreamsSignal,
    activeSpeakersSignal,
} from '../signals/Meeting.signals';
import type {RoomParticipantsWithNoDirectChats} from '../types';
import {
    GroupKey,
    inMeetingParticipantsUpdates,
    isAPIHost,
    toBreakoutGroup,
} from '../utils/createParticipants';
import {combineSubscribes, type Subscribe} from '../utils/subscribe';

export const useMeetingContext = () => useAssertedContext(MeetingContext);

export const onParticipantLeft = combineSubscribes(
    infinityClientSignals.onParticipants.add,
    infinityClientSignals.onParticipantLeft.add,
    infinityClientSignals.onTransfer.add,
    inMeetingParticipantsUpdates.add,
);

export const onBreakoutRoomsChange = combineSubscribes(
    infinityClientSignals.onBreakoutBegin.add,
    infinityClientSignals.onBreakoutEnd.add,
);

export const onRoomChange = combineSubscribes(
    infinityClientSignals.onTransfer.add,
    infinityClientSignals.onBreakoutRefer.add,
);

export const onParticipantsChange = combineSubscribes(
    infinityClientSignals.onParticipants.add,
    infinityClientSignals.onParticipantUpdated.add,
    infinityClientSignals.onParticipantJoined.add,
    infinityClientSignals.onParticipantLeft.add,
    infinityClientSignals.onBreakoutEnd.add,
    inMeetingParticipantsUpdates.add,
);

const onPresenterChange = combineSubscribes(
    onParticipantsChange,
    onRoomChange,
    callSignals.onCallConnected.add,
);

export const usePresenterName = (
    getState: Meeting['getPresenter'],
    subscribe: Subscribe = onPresenterChange,
) => useSyncExternalStore(subscribe, () => getState()?.displayName);

export const onConferentStatusChange = combineSubscribes(
    infinityClientSignals.onConferenceStatus.add,
    infinityClientSignals.onBreakoutEnd.add,
);

export const useStep = (
    getState: Meeting['getStep'],
    subscribe: Subscribe = stepSignal.add,
) => useSyncExternalStore(subscribe, getState);

export const useRemoteStream = (
    getState: Meeting['getRemoteStream'],
    subscribe: Subscribe = remoteStreamSignal.add,
) => useSyncExternalStore(subscribe, getState);

/**
 * Subscribes to the participants who get a tile on the switching stage. The
 * list keeps its identity until participants are added, removed or reordered.
 *
 * @example
 * const participantIds = useStageParticipantIds();
 */
export const useStageParticipantIds = (
    subscribe: Subscribe = stageParticipantIdsSignal.add,
) => {
    const meeting = useMeetingContext();
    return useSyncExternalStore(subscribe, meeting.getStageParticipantIds);
};

/**
 * Subscribes to a single participant's switching-call video. Re-renders only
 * when that participant's video stream is replaced.
 *
 * @example
 * const stream = useParticipantVideoStream(participantId);
 */
export const useParticipantVideoStream = (
    participantId: ParticipantID,
    subscribe: Subscribe = participantVideoStreamsSignal.add,
) => {
    const meeting = useMeetingContext();
    return useSyncExternalStore(subscribe, () =>
        meeting.getParticipantVideoStream(participantId),
    );
};

/**
 * Subscribes to the remote video streams of a switching call, keyed by
 * participant. The transcoded mix is keyed by `MIX_PARTICIPANT_UUID`.
 *
 * @example
 * const videoStreams = useParticipantVideoStreams();
 */
export const useParticipantVideoStreams = (
    subscribe: Subscribe = participantVideoStreamsSignal.add,
) => {
    const meeting = useMeetingContext();
    return useSyncExternalStore(subscribe, meeting.getParticipantVideoStreams);
};

/**
 * Subscribes to the remote audio streams of a switching call, keyed by
 * participant. The transcoded mix is keyed by `MIX_PARTICIPANT_UUID`.
 *
 * @example
 * const audioStreams = useParticipantAudioStreams();
 */
export const useParticipantAudioStreams = (
    subscribe: Subscribe = participantAudioStreamsSignal.add,
) => {
    const meeting = useMeetingContext();
    return useSyncExternalStore(subscribe, meeting.getParticipantAudioStreams);
};

/**
 * Subscribes to whether a single participant is speaking in a switching call.
 *
 * @example
 * const isSpeaking = useIsActiveSpeaker(participantId);
 */
export const useIsActiveSpeaker = (
    participantId: ParticipantID,
    subscribe: Subscribe = activeSpeakersSignal.add,
) => {
    const meeting = useMeetingContext();
    return useSyncExternalStore(subscribe, () =>
        meeting.isActiveSpeaker(participantId),
    );
};

export const usePinRequired = (
    getState: Meeting['getPinRequired'],
    subscribe: Subscribe = pinRequiredSignal.add,
) => useSyncExternalStore(subscribe, getState);

export const useIdps = (
    getState: Meeting['getIdps'],
    subscribe: Subscribe = idpSignal.add,
) => useSyncExternalStore(subscribe, getState);

export const useLayoutOverlayEnabled = (
    getState: Meeting['getLayoutOverlayEnabled'],
    subscribe: Subscribe = layoutOverlayTextEnabledSignal.add,
) => useSyncExternalStore(subscribe, getState);

export const useSplashScreen = (
    getState: Meeting['getSplashScreen'],
    subscribe: Subscribe = splashScreenSignal.add,
) => useSyncExternalStore(subscribe, getState);

export const useBreakoutsAskingForHelp = (
    subscribe: Subscribe = breakoutsAskingForHelpSignal.add,
) => {
    const meeting = useMeetingContext();
    return useSyncExternalStore(subscribe, meeting.getBreakoutsAskingForHelp);
};

export const useGuestTransferDetails = (
    subscribe: Subscribe = guestTransferDetailsSignal.add,
) => {
    const meeting = useMeetingContext();
    return useSyncExternalStore(subscribe, meeting.getGuestTransferDetails);
};

export const useHostReferDetails = (
    subscribe: Subscribe = hostReferDetailsSignal.add,
) => {
    const meeting = useMeetingContext();
    return useSyncExternalStore(subscribe, meeting.getHostReferDetails);
};

export const useRemoteAlias = (
    subscribe: Subscribe = infinityClientSignals.onAuthenticatedWithConference
        .add,
) => {
    const meeting = useMeetingContext();
    return useSyncExternalStore(subscribe, meeting.getRemoteAlias);
};

export const useMeetingError = (
    subscribe: Subscribe = infinityErrorSignal.add,
) => {
    const meeting = useMeetingContext();
    return useSyncExternalStore(subscribe, meeting.getError);
};

export const useIsFeccEnabled = (
    getState: Meeting['getIsFeccEnabled'],
    subscribe: Subscribe = onParticipantsChange,
) => useSyncExternalStore(subscribe, getState);

export const useCurrentRoomFeccParticipantIDs = (
    subscribe: Subscribe = onParticipantsChange,
) => {
    const meeting = useMeetingContext();
    const subscribes = combineSubscribes(
        subscribe,
        meeting.getCurrentFeccPidsSignalState().add,
    );
    return useSyncExternalStore(subscribes, () => {
        return meeting.getCurrentRoomFeccParticipantIDs();
    });
};

export const useCurrentHostLayout = (
    getState: Meeting['getCurrentHostLayout'],
    subscribe: Subscribe = infinityClientSignals.onRequestedLayout.add,
) => useSyncExternalStore(subscribe, getState);

export const useEveryoneChatMessages = (
    getState: Meeting['getChatMessages'],
    subscribe: Subscribe = chatMessagesSignal.add,
) => useSyncExternalStore(subscribe, getState);

export const useUnreadChatMessages = (
    getState: Meeting['getUnreadChatMessages'],
    subscribe: Subscribe = unreadChatMessagesSignal.add,
) => useSyncExternalStore(subscribe, getState);

export const useDirectChatMessages = (
    getState: Meeting['getDirectChatMessages'],
    subscribe: Subscribe = directChatMessagesSignal.add,
) => useSyncExternalStore(subscribe, getState);

export const useUnreadDirectChatMessages = (
    getState: Meeting['getUnreadDirectChatMessages'],
    subscribe: Subscribe = unreadDirectChatMessagesSignal.add,
) => useSyncExternalStore(subscribe, getState);

export const useUnseenUnreadDirectChatMessages = (
    getState: Meeting['getUnseenUnreadDirectChatMessages'],
    subscribe: Subscribe = unseenUnreadDirectChatMessagesSignal.add,
) => useSyncExternalStore(subscribe, getState);

export const useIsPresentationInMixActive = (
    getState: Meeting['getIsPresentationInMixActive'],
    subscribe: Subscribe = infinityClientSignals.onLayoutUpdate.add,
) => useSyncExternalStore(subscribe, getState);

export const useAllUnreadAndUnseenChatMessages = (
    unreadChatMessages: ChatMessage[],
    unseenUnreadDirectChatMessages: UnseenUnreadDirectChatMessages,
) =>
    useMemo(
        () => [...unreadChatMessages, ...unseenUnreadDirectChatMessages],
        [unreadChatMessages, unseenUnreadDirectChatMessages],
    );

export const useChatMessages = (
    meeting: ReturnType<typeof useMeetingContext>,
    selectedChatType: ChatType,
    participantInDirectChatWith: Participant | undefined,
) => {
    const everyoneChatMessages = useEveryoneChatMessages(
        meeting.getChatMessages,
    );
    const directChatMessages = useDirectChatMessages(
        meeting.getDirectChatMessages,
    );

    const currentChatMessages = useMemo(
        () =>
            selectedChatType === ChatType.Everyone
                ? everyoneChatMessages
                : participantInDirectChatWith
                  ? (directChatMessages.get(participantInDirectChatWith.uuid) ??
                    [])
                  : [],
        [
            directChatMessages,
            everyoneChatMessages,
            participantInDirectChatWith,
            selectedChatType,
        ],
    );

    return {everyoneChatMessages, directChatMessages, currentChatMessages};
};

export const useParticipantRoomID = (
    participantId?: ParticipantID,
    subscribe: Subscribe = onParticipantsChange,
) => {
    const meeting = useMeetingContext();
    return useSyncExternalStore(subscribe, () =>
        meeting.getParticipantRoomID(participantId),
    );
};

export const useRoomID = (subscribe: Subscribe = onRoomChange) => {
    const meeting = useMeetingContext();
    return useSyncExternalStore(subscribe, meeting.getRoomId);
};

export const useBreakoutParticipants = (
    subscribe: Subscribe = onParticipantsChange,
) => {
    const meeting = useMeetingContext();
    const getParticipants = memoize(
        () => {
            return new Map<RoomID, InMeetingParticipant[]>(
                meeting.getRooms().map(roomId => {
                    const participants: InMeetingParticipant[] = [];
                    for (const pid of meeting.getSupportedBreakoutParticipants({
                        roomId,
                    })) {
                        const participant = meeting.getParticipant(pid, roomId);
                        if (participant) {
                            participants.push(participant);
                        }
                    }
                    return [roomId, participants];
                }),
            );
        },
        () =>
            meeting
                .getRooms()
                .map(roomId =>
                    meeting.getSupportedBreakoutParticipants({roomId}),
                ),
    );
    return useSyncExternalStore(subscribe, getParticipants);
};

export const useBreakoutRoomsSize = (
    subscribe: Subscribe = onBreakoutRoomsChange,
) => {
    const meeting = useMeetingContext();
    return useSyncExternalStore(subscribe, () => meeting.getRooms().length - 1);
};

export const useBreakoutNames = (
    subscribe: Subscribe = onConferentStatusChange,
) => {
    const meeting = useMeetingContext();
    return useSyncExternalStore(subscribe, () =>
        meeting.getBreakoutRoomNames(),
    );
};

export const getParticipantsByGroup = (
    meeting: Meeting,
    filterBy: GroupKey,
    searchQuery?: string,
) => {
    const roomId = meeting.getRoomId();
    const isBreakout = !!meeting.getConferenceStatus().get(roomId)?.breakout;
    const filter = isBreakout ? toBreakoutGroup(filterBy) : filterBy;
    return meeting.getParticipants({roomId, filterBy: filter, searchQuery});
};

export const createParticipantGroupHook =
    (filterBy: GroupKey, applySearch?: boolean) =>
    (
        subscribe = applySearch
            ? combineSubscribes(
                  onParticipantsChange,
                  participantSearchQuerySignal.add,
              )
            : onParticipantsChange,
    ) => {
        const meeting = useMeetingContext();
        return useSyncExternalStore(subscribe, () =>
            getParticipantsByGroup(
                meeting,
                filterBy,
                applySearch === true ? meeting.getSearchQuery() : undefined,
            ),
        );
    };

export const createParticipantGroupSizeHook = (filterBy: GroupKey) => {
    const hook = createParticipantGroupHook(filterBy);
    return () => hook().size;
};

export const createParticipantGroupSizeExcludSelfHook =
    (filterBy: GroupKey) =>
    (subscribe = onParticipantsChange) => {
        const meeting = useMeetingContext();
        return useSyncExternalStore(subscribe, () => {
            const pids = meeting.getParticipants({
                roomId: meeting.getRoomId(),
                filterBy,
            });
            const selfUUID = meeting.getMe()?.uuid;
            if (selfUUID && pids.has(selfUUID)) {
                return pids.size - 1;
            }
            return pids.size;
        });
    };

export const useParticipant = (
    participantId: ParticipantID,
    roomId: RoomID,
    subscribe: Subscribe = combineSubscribes(
        change => {
            return infinityClientSignals.onParticipantUpdated.add(events => {
                if (
                    events.some(evt => {
                        return (
                            roomId === evt.id &&
                            evt.participant.uuid === participantId
                        );
                    })
                ) {
                    change();
                }
            });
        },
        change => {
            return infinityClientSignals.onStage.add(stages => {
                if (stages.some(s => s.userId === participantId)) {
                    change();
                }
            });
        },
    ),
) => {
    const meeting = useMeetingContext();
    return useSyncExternalStore(subscribe, () => {
        return meeting.getParticipant(participantId, roomId);
    });
};

export const useInMeetingParticipants = createParticipantGroupHook(
    GroupKey.InMeeting,
);
export const useInMeetingParticipantsApplySearch = createParticipantGroupHook(
    GroupKey.InMeeting,
    true,
);
export const useInMeetingParticipantsSize = createParticipantGroupSizeHook(
    GroupKey.InMeeting,
);
export const useWaitingInLobbyParticipants = createParticipantGroupHook(
    GroupKey.WaitingInLobby,
);
export const useWaitingInLobbyParticipantsApplySearch =
    createParticipantGroupHook(GroupKey.WaitingInLobby, true);
export const useWaitingInLobbyParticipantsSize = createParticipantGroupSizeHook(
    GroupKey.WaitingInLobby,
);
export const useRaisedHandParticipants = createParticipantGroupHook(
    GroupKey.RaisedHand,
);
export const useRaisedHandParticipantsApplySearch = createParticipantGroupHook(
    GroupKey.RaisedHand,
    true,
);
export const useRaisedHandParticipantsSize = createParticipantGroupSizeHook(
    GroupKey.RaisedHand,
);
export const useRaisedHandParticipantsSizeExcludeSelf =
    createParticipantGroupSizeExcludSelfHook(GroupKey.RaisedHand);
export const useExternalParticipants = createParticipantGroupHook(
    GroupKey.External,
);
export const useExternalParticipantsApplySearch = createParticipantGroupHook(
    GroupKey.External,
    true,
);
export const useExternalParticipantsSize = createParticipantGroupSizeHook(
    GroupKey.External,
);

export const useParticipantDisconnected = (
    participantId: ParticipantID | undefined,
    roomID?: RoomID,

    subscribe: Subscribe = onParticipantLeft,
) => {
    const meeting = useMeetingContext();
    return useSyncExternalStore(subscribe, () => {
        if (!participantId) {
            return true;
        }
        return meeting.isParticipantDisconnected(participantId, roomID);
    });
};

// `onParticipantsChange` already covers `onBreakoutEnd`; a signal throws when
// the same observer is added twice.
const onParticipantsOrRoomsChange = combineSubscribes(
    onParticipantsChange,
    infinityClientSignals.onBreakoutBegin.add,
    onRoomChange,
);

/**
 * Lists, per room, the in-meeting participants the current user has no direct
 * chat with yet, split by direct chat support.
 *
 * Hosts get every open room, main room first, because they can see
 * participants in all breakout rooms. Everyone else gets only their current
 * room. The current user's identity in each room is excluded, and so are hosts
 * without media in rooms other than the current one, because Infinity creates
 * such an identity in every breakout room for each host.
 *
 * @param directChatMessages - Existing direct chats; their participants are
 * excluded.
 * @param subscribe - Store subscription; defaults to participant and room
 * changes.
 *
 * @example
 * const rooms = useRoomsParticipantsWithNoDirectChats(directChatMessages);
 * const isGroupedByRoom = rooms.length > 1;
 */
export const useRoomsParticipantsWithNoDirectChats = (
    directChatMessages: DirectChatMessages,
    subscribe: Subscribe = onParticipantsOrRoomsChange,
) => {
    const meeting = useMeetingContext();
    const getVisibleRoomIds = (): RoomID[] =>
        meeting.getMe()?.isHost ? meeting.getRooms() : [meeting.getRoomId()];
    const getInMeetingParticipants = (roomId: RoomID) =>
        meeting.getParticipants({filterBy: GroupKey.InMeeting, roomId});

    const getRoomsParticipants = memoize(
        () => {
            const currentRoomId = meeting.getRoomId();
            const myParticipantIDs = new Set<ParticipantID | undefined>([
                meeting.getMe()?.uuid,
                meeting.getMe(MAIN_ROOM_ID)?.uuid,
                ...Array.from(
                    meeting.getBreakoutRooms().values(),
                    ({participant_uuid}) => participant_uuid,
                ),
            ]);
            return getVisibleRoomIds().map(
                (roomId): RoomParticipantsWithNoDirectChats => {
                    const participantsWithNoDirectChats: ParticipantID[] = [];
                    const unsupportedParticipantsWithNoDirectChats: ParticipantID[] =
                        [];
                    for (const participantID of getInMeetingParticipants(
                        roomId,
                    )) {
                        if (
                            myParticipantIDs.has(participantID) ||
                            directChatMessages.has(participantID)
                        ) {
                            continue;
                        }
                        const participant = meeting.getParticipant(
                            participantID,
                            roomId,
                        );
                        if (
                            participant &&
                            roomId !== currentRoomId &&
                            isAPIHost(participant)
                        ) {
                            continue;
                        }
                        if (participant?.supportsDirectChat) {
                            participantsWithNoDirectChats.push(participantID);
                        } else {
                            unsupportedParticipantsWithNoDirectChats.push(
                                participantID,
                            );
                        }
                    }
                    return {
                        roomId,
                        participantsWithNoDirectChats,
                        unsupportedParticipantsWithNoDirectChats,
                    };
                },
            );
        },
        // Primitive and store-cached values only, so the snapshot stays stable
        // between store reads while nothing changes.
        () => {
            const isHost = !!meeting.getMe()?.isHost;
            const rooms = meeting.getRooms();
            const roomId = meeting.getRoomId();
            const visibleRoomIds = isHost ? rooms : [roomId];
            return [
                isHost,
                rooms,
                roomId,
                meeting.getBreakoutRooms(),
                ...visibleRoomIds.map(getInMeetingParticipants),
            ];
        },
    );

    return useSyncExternalStore(subscribe, getRoomsParticipants);
};

/**
 * A state to indicate whether the user is capable to send presentation slides
 */
export const useAbleToPresent = () => {
    const config = useConfigContext();
    const meeting = useMeetingContext();
    return useSyncExternalStore(
        onChange => config.subscribe('callType', onChange),
        () => meeting.ableToPresent(),
    );
};

/**
 * A state to indicate whether the participant is capable of sending presentation slides
 */
export const useAllowedToPresent = (
    roomId?: RoomID,
    subscribe = combineSubscribes(
        onConferentStatusChange,
        infinityClientSignals.onMe.add,
    ),
) => {
    const meeting = useMeetingContext();
    return useSyncExternalStore(subscribe, () =>
        meeting.allowedToPresent(roomId),
    );
};

export const useChatEnabled = (
    roomId?: RoomID,
    subscribe = onConferentStatusChange,
) => {
    const meeting = useMeetingContext();
    return useSyncExternalStore(subscribe, () =>
        meeting.getChatEnabled(roomId),
    );
};

export const useLiveCaptions = (
    getState: Meeting['getLiveCaptions'],
    subscribe: Subscribe = liveCaptionsSignal.add,
) => useSyncExternalStore(subscribe, getState);

export const useLiveCaptionsEnabled = (
    getState: Meeting['getLiveCaptionsEnabled'],
    subscribe: Subscribe = liveCaptionsEnabledSignal.add,
) => useSyncExternalStore(subscribe, getState);

export const useSelectedCaptionsLanguage = (
    getState: Meeting['getSelectedCaptionsLanguage'],
    subscribe: Subscribe = selectedCaptionsLanguageSignal.add,
) => useSyncExternalStore(subscribe, getState);

export const useIsTogglingLiveCaptions = (
    getState: Meeting['getIsTogglingLiveCaptions'],
    subscribe: Subscribe = isTogglingLiveCaptionsSignal.add,
) => useSyncExternalStore(subscribe, getState);

export const useLiveCaptionsAvailable = () => {
    const meeting = useMeetingContext();
    const isLiveCaptionsAvailable = useSyncExternalStore(
        infinityClientSignals.onConferenceStatus.add,
        meeting.getLiveCaptionsAvailability,
    );

    return isLiveCaptionsAvailable && applicationConfig.showLiveCaptionsFeature;
};

export const useAvailableCaptionsLanguages = () => {
    const meeting = useMeetingContext();
    return useSyncExternalStore(
        onConferentStatusChange,
        meeting.getAvailableCaptionsLanguages,
    );
};

export const useSourceCaptionsLanguages = () => {
    const meeting = useMeetingContext();
    return useSyncExternalStore(
        onConferentStatusChange,
        meeting.getSourceCaptionsLanguages,
    );
};
